// RX-CAL (최종설계안_RX §8) — 청약 일정 이벤트 판정. 순수 함수(RULES#143).
//
// 일정 «데이터 소스» 는 상세 일정 섹션(lib/apt/schedule)과 같은 apt_subscriptions 열이다 — 한 데이터 한 자리.
// ⛔ 없는 날짜를 지어내지 않는다: 열이 비면 그 이벤트는 없다.
// ⛔ 마퀴·자동 스크롤 없음 — 정적 행.

export type ScheduleKind = 'rcept' | 'winner' | 'contract';

export interface SubDates {
  id: number | string;
  name: string;
  region?: string | null;
  addr?: string | null;
  rcept_bgnde?: string | null;
  rcept_endde?: string | null;
  przwner_presnatn_de?: string | null;
  cntrct_cncls_bgnde?: string | null;
  cntrct_cncls_endde?: string | null;
}

export interface ScheduleEvent {
  key: string;
  subId: number | string;
  name: string;
  region: string | null;
  kind: ScheduleKind;
  /** 칩 라벨: 접수중 · 접수 예정 · 발표 · 계약. */
  label: string;
  /** stage 톤 선택용 — tone.ts 의 이름만. */
  tone: 'stageOpen' | 'stageAward' | 'stageBuild' | 'stageClosed';
  date: string; // YYYY-MM-DD (정렬 기준일)
  /** 오늘 기준 일수. 접수중은 마감까지, 그 외는 도래까지. 음수=지남. */
  dday: number;
  done: boolean;
  /** live2 c — 접수 이벤트의 접수 기간(YYYY-MM-DD). 접수 외 종류는 없음. 「이번 주 청약」 = /apt 30일 표와 같은 정의(rceptOverlaps). */
  rceptBgn?: string;
  rceptEnd?: string;
}

export function normYmd(v: string | null | undefined): string | null {
  if (!v) return null;
  const s = String(v).trim();
  const m8 = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (m8) return `${m8[1]}-${m8[2]}-${m8[3]}`;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

/** 「D-3」「D-Day」「D+2」. */
export function ddayText(n: number): string {
  if (n === 0) return 'D-Day';
  return n > 0 ? `D-${n}` : `D+${-n}`;
}

const KIND_ORDER: Record<ScheduleKind, number> = { rcept: 0, winner: 1, contract: 2 };

/**
 * 이벤트 종류별로 단지당 최대 1건. 창: 오늘−pastDays ~ 오늘+windowDays.
 * 지난 일정은 done(완료 톤)으로 남기되 목록 뒤로 보낸다.
 */
export function buildScheduleEvents(
  subs: readonly SubDates[],
  today: string,
  opts: { windowDays?: number; pastDays?: number } = {},
): ScheduleEvent[] {
  const win = opts.windowDays ?? 30;
  const past = opts.pastDays ?? 2;
  const out: ScheduleEvent[] = [];
  for (const s of subs) {
    if (!s?.name) continue;
    const base = { subId: s.id, name: s.name, region: s.region ?? null };

    const bgn = normYmd(s.rcept_bgnde);
    const end = normYmd(s.rcept_endde) ?? bgn;
    if (bgn && end) {
      const rc = { rceptBgn: bgn, rceptEnd: end };
      if (today < bgn) {
        const d = daysBetween(today, bgn);
        if (d <= win) out.push({ ...base, ...rc, key: `${s.id}:rcept`, kind: 'rcept', label: '접수 예정', tone: 'stageOpen', date: bgn, dday: d, done: false });
      } else if (today <= end) {
        out.push({ ...base, ...rc, key: `${s.id}:rcept`, kind: 'rcept', label: '접수중', tone: 'stageOpen', date: end, dday: daysBetween(today, end), done: false });
      } else if (daysBetween(end, today) <= past) {
        out.push({ ...base, ...rc, key: `${s.id}:rcept`, kind: 'rcept', label: '접수 마감', tone: 'stageClosed', date: end, dday: daysBetween(today, end), done: true });
      }
    }

    const win0 = normYmd(s.przwner_presnatn_de);
    if (win0) {
      const d = daysBetween(today, win0);
      if (d >= 0 && d <= win) out.push({ ...base, key: `${s.id}:winner`, kind: 'winner', label: '발표', tone: 'stageAward', date: win0, dday: d, done: false });
      else if (d < 0 && -d <= past) out.push({ ...base, key: `${s.id}:winner`, kind: 'winner', label: '발표 완료', tone: 'stageClosed', date: win0, dday: d, done: true });
    }

    const ct = normYmd(s.cntrct_cncls_bgnde);
    if (ct) {
      const d = daysBetween(today, ct);
      if (d >= 0 && d <= win) out.push({ ...base, key: `${s.id}:contract`, kind: 'contract', label: '계약', tone: 'stageBuild', date: ct, dday: d, done: false });
    }
  }
  out.sort((a, b) =>
    Number(a.done) - Number(b.done)
    || (a.done ? b.dday - a.dday : a.dday - b.dday)
    || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]
    || a.name.localeCompare(b.name, 'ko'));
  return out;
}

/** 지역 우선 정렬(§8-1) — 선택 지역(시/도 이름 키)이 앞, 상대 순서는 유지. 선택 없음=그대로(전국). */
export function prioritizeRegion<T extends { region: string | null }>(events: readonly T[], regionKeys: readonly string[] | null | undefined): T[] {
  if (!regionKeys || !regionKeys.length) return [...events];
  const set = new Set(regionKeys);
  const inR = events.filter((e) => e.region && set.has(e.region));
  const rest = events.filter((e) => !(e.region && set.has(e.region)));
  return [...inR, ...rest];
}

/**
 * live2 c 지시 13 — 「청약 일정 N일」 정의 한 벌: 접수 기간이 [오늘, 오늘+days] 와 겹친다
 *   = 접수 마감(없으면 시작) ≥ 오늘 · 접수 시작 ≤ 오늘+days. /apt 「30일 청약 일정」 표(region-doc scheduleUncached:
 *   rcept_endde ≥ today · rcept_bgnde ≤ today+30)와 같은 조건이다. 홈 「이번 주 청약」 = days 6(오늘 포함 7일) — 그래서 언제나 30일 표의 부분집합.
 */
export function rceptOverlaps(bgn: string | null | undefined, end: string | null | undefined, today: string, days: number): boolean {
  const b = normYmd(bgn);
  if (!b) return false;
  const e = normYmd(end) ?? b;
  const last = new Date(Date.parse(`${today}T00:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
  return e >= today && b <= last;
}

/** 이벤트 목록 → 접수 기간이 창과 겹치는 «청약 수»(subId 서로 다른 것). 현장 연결 여부와 무관(/apt 표도 링크 없는 행을 센다). */
export function countRceptInWindow(events: ReadonlyArray<Pick<ScheduleEvent, 'kind' | 'subId' | 'rceptBgn' | 'rceptEnd'>>, today: string, days: number): number {
  const ids = new Set<string>();
  for (const e of events) if (e.kind === 'rcept' && rceptOverlaps(e.rceptBgn, e.rceptEnd, today, days)) ids.add(String(e.subId));
  return ids.size;
}
